import React, { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataContext';
import { CalendarCheck, UserPlus, Package, IndianRupee, CreditCard, ShoppingBag } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PujasTab from '../components/admin/PujasTab';
import PanditsTab from '../components/admin/PanditsTab';
import SamagriTab from '../components/admin/SamagriTab';
import ProductsTab from '../components/admin/ProductsTab';
import BookingsTab from '../components/admin/BookingsTab';
import PaymentsTab from '../components/admin/PaymentsTab';
import SettingsTab from '../components/admin/SettingsTab';
import SEOTab from '../components/admin/SEOTab';

export default function AdminDashboard() {
  const { user } = useAuth();
  const { pujas, pandits, products, bookings, settings } = useData();
  const navigate = useNavigate();

  useEffect(() => {
    if (user === false || (user && user.role !== 'admin')) navigate('/admin/login');
  }, [user, navigate]);

  const revenue = useMemo(
    () => bookings.filter((b) => ['paid', 'confirmed', 'completed'].includes(b.status))
      .reduce((s, b) => s + (+b.amount || 0), 0),
    [bookings]
  );

  const stats = [
    { icon: CalendarCheck, label: 'Total Pujas', value: pujas.length, color: 'from-orange-500 to-red-500' },
    { icon: UserPlus, label: 'Pandit Jis', value: pandits.length, color: 'from-purple-500 to-pink-500' },
    { icon: ShoppingBag, label: 'Products', value: products.length, color: 'from-fuchsia-500 to-pink-500' },
    { icon: Package, label: 'Bookings', value: bookings.length, color: 'from-amber-500 to-orange-500' },
    { icon: IndianRupee, label: 'Revenue', value: `₹${revenue.toLocaleString('en-IN')}`, color: 'from-green-500 to-emerald-500' },
  ];

  if (!user || user.role !== 'admin') return null;

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 md:py-10" data-testid="admin-dashboard">
      <div className="mb-6 md:mb-8 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-nv-wine">Admin Dashboard</h1>
          <p className="text-nv-muted text-sm">Full control over pujas, pandits, payments & bookings</p>
        </div>
        <div className="flex items-center gap-2">
          <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-semibold ${settings.razorpayEnabled ? 'bg-green-100 text-green-700' : 'bg-stone-100 text-stone-600'}`}>
            <CreditCard className="w-3 h-3" />
            Razorpay: {settings.razorpayEnabled ? `${String(settings.razorpayMode || 'test').toUpperCase()} • Active` : 'Disabled'}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 md:gap-4 mb-6 md:mb-8">
        {stats.map((s, i) => (
          <div key={i} className="bg-white border border-nv-gold/20 rounded-2xl p-3 md:p-5 flex items-center gap-3 shadow-sm">
            <div className={`w-11 h-11 md:w-14 md:h-14 rounded-2xl bg-gradient-to-br ${s.color} flex items-center justify-center text-white shrink-0`}>
              <s.icon className="w-5 h-5 md:w-6 md:h-6" />
            </div>
            <div className="min-w-0">
              <div className="text-lg md:text-2xl font-bold text-nv-wine">{s.value}</div>
              <div className="text-xs md:text-sm text-nv-muted truncate">{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="pujas" className="w-full">
        <TabsList className="bg-orange-100/60 flex-wrap h-auto">
          <TabsTrigger value="pujas" data-testid="tab-pujas">Pujas</TabsTrigger>
          <TabsTrigger value="pandits" data-testid="tab-pandits">Pandit Jis</TabsTrigger>
          <TabsTrigger value="products" data-testid="tab-products">Products ({products.length})</TabsTrigger>
          <TabsTrigger value="samagri" data-testid="tab-samagri">Samagri</TabsTrigger>
          <TabsTrigger value="bookings" data-testid="tab-bookings">Bookings ({bookings.length})</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
        </TabsList>

        <TabsContent value="pujas" className="mt-6"><PujasTab /></TabsContent>
        <TabsContent value="pandits" className="mt-6"><PanditsTab /></TabsContent>
        <TabsContent value="products" className="mt-6"><ProductsTab /></TabsContent>
        <TabsContent value="samagri" className="mt-6"><SamagriTab /></TabsContent>
        <TabsContent value="bookings" className="mt-6"><BookingsTab /></TabsContent>
        <TabsContent value="payments" className="mt-6"><PaymentsTab /></TabsContent>
        <TabsContent value="settings" className="mt-6"><SettingsTab /></TabsContent>
        <TabsContent value="seo" className="mt-6"><SEOTab /></TabsContent>
      </Tabs>
    </div>
  );
}
